import "./LessonModal.scss";

import React, { useEffect, useState } from "react";
import { createLesson, languageTaught } from "../../features/lessonApi";
import { useMutation, useQuery } from "@tanstack/react-query";

import { API_URL } from "../../utils/constants";
import { ClipLoader } from "react-spinners";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "react-modal";
import axios from "axios";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    height: "70%",
    width: "65%",
    paddingHorizontal: "20px",
    paddingVertical: "24px",
    backgroundColor: "#fff",
    borderRadius: "5px",
    borderColor: "white",
  },
};

const inputStyle = {
  width: "80%",
  padding: "10px",
  marginBottom: "10px",
  border: "1px solid #ccc",
  borderRadius: "9px",
  fontSize: "16px",
};

const buttonStyle = {
  padding: "10px 15px",
  marginTop: "10px",
  backgroundColor: "#007bff",
  color: "#fff",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "5px",
};

const errorStyle = {
  color: "red",
  fontSize: "12px",
  margin: 0,
};

const addPackageButtonStyle = {
  ...buttonStyle,
  backgroundColor: "#28a745",
  marginRight: "10px",
};

const removePackageButtonStyle = {
  ...buttonStyle,
  backgroundColor: "#dc3545",
};

export const LessonModal = (props: any) => {
  const queryClient = useQueryClient();
  // State for form inputs
  const [lessonName, setLessonName] = useState("");
  const [description, setDescription] = useState("");
  const [minPrice, setMinPrice] = useState<any>("");
  const [packages, setPackages] = useState<any>([
    { price: "", duration: 30, quantity: "" },
  ]);
  const [language, setLanguage] = useState("");
  const [values, setValues] = useState<any>({
    data: [],
    error: "",
    loading: false,
    submitError: "",
  });

  const clearHandler = () => {
    setLessonName("");
    setDescription("");
    setMinPrice("");
    setPackages([{ price: "", duration: 30, quantity: "" }]);
    setLanguage("");
    setValues({
      // Corrected reset
      data: [],
      error: "",
      loading: false,
      submitError: "",
    });
  };

  const [teachable, setTeachable] = useState<string[]>([]);

  const {
    data: languages,
    isError,
    isSuccess,
    error: langError,
  } = useQuery({
    queryKey: ["getLanguages"],
    queryFn: languageTaught,
  });

  useEffect(() => {
    if (isSuccess) {
      const teachableLanguage = languages?.result?.languagesTaught.map(
        (item: any) => item?.language,
      );

      setTeachable(teachableLanguage);
    }
  }, [languages]);

  const newErrors: any = {};
  const validate = () => {
    if (!lessonName?.trim()) newErrors.lessonName = "Lesson Name is required.";
    if (!description.trim()) newErrors.description = "Description is required.";
    if (!minPrice || parseFloat(minPrice) <= 0)
      newErrors.minPrice = "Min Price must be a positive number.";
    if (parseFloat(minPrice) < 8)
      newErrors.minPrice = "Min Price must be more than 8 (USD)";
    if (parseFloat(minPrice) > 70)
      newErrors.minPrice = "Min Price must not be more than 70 (USD)";
    if (!language) newErrors.language = "Please select a language."; // Validation for language dropdown

    packages.forEach((pkg: any, index: any) => {
      if (!pkg.price || parseFloat(pkg.price) <= 0)
        newErrors[`packagePrice_${index}`] = "Price must be a positive number.";
      if (!pkg.duration)
        newErrors[`packageDuration_${index}`] = "Please select a duration.";
      if (!pkg.quantity || parseInt(pkg.quantity) <= 0)
        newErrors[`packageQuantity_${index}`] =
          "Quantity must be a positive integer.";
    });

    setValues({ ...values, error: newErrors });
    return Object.keys(newErrors).length === 0;
  };

  // Add a new package
  const addPackage = () => {
    setPackages([...packages, { price: "", duration: 30, quantity: "" }]);
  };

  // Remove a package by index
  const removePackage = (index: number) => {
    if (index === 0) return;
    const updatedPackages = packages.filter(
      (_: any, pkgIndex: any) => pkgIndex !== index,
    );
    setPackages(updatedPackages);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      create({
        name: lessonName,
        type: "standard",
        languageId: parseInt(language),
        description,
        minPrice: parseInt(minPrice),
        packages,
      });
    }
  };

  const { mutateAsync: create, isPending } = useMutation({
    mutationFn: createLesson,

    onSuccess: (data: any) => {
      toast.success("Lesson Created", {
        richColors: true,
      });
      clearHandler();
      props.closeModal();
      queryClient.invalidateQueries({ queryKey: ["lessons"] });
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to create lesson");
    },
  });
  // Handle package input change
  const handlePackageChange = (
    index: number,
    field: "price" | "duration" | "quantity",
    value: number,
  ) => {
    const updatedPackages = [...packages];
    updatedPackages[index][field] = value;
    setPackages(updatedPackages);
  };

  useEffect(() => {
    packages.forEach((pack: any, index: number) => {
      if (Number(pack.price) < Number(minPrice)) {
        newErrors[`packagePrice_${index}`] =
          "price must not be lower than the min price .";
      }
      setValues({ ...values, error: newErrors });
    });
  }, [packages]);
  return (
    <Modal
      isOpen={props.modalIsOpen}
      onRequestClose={props.closeModal}
      overlayClassName="lessonModal"
      className="modalContent"
      contentLabel="Lesson Modal"
    >
      <span className="closeButton" onClick={props.closeModal}>
        ×
      </span>

      <form onSubmit={handleSubmit}>
        <div className="formGrid">
          {/* Left side: Lesson details */}
          <div className="formSection">
            <p className="sectionTitle">Lesson Details</p>

            <label className="formLabel">
              Lesson Title
              <p className="inputSubtitle">
                The Name of the lesson eg. Introductory Igbo Lesson
              </p>
              <input
                placeholder="Lesson Title"
                type="text"
                value={lessonName}
                onChange={(e) => setLessonName(e.target.value)}
              />
              {values.error.lessonName && (
                <p className="error">{values.error.lessonName}</p>
              )}
            </label>

            <label className="formLabel">
              Lesson Description
              <p className="inputSubtitle">
                What do students expect to learn from this lesson.
              </p>
              <p className="charCount">{description?.length}/1000</p>
              <textarea
                placeholder="Lesson Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              {values.error.description && (
                <p className="error">{values.error.description}</p>
              )}
            </label>

            <label className="formLabel">
              Select Language
              <p className="inputSubtitle">
                What language does this lesson fall under
              </p>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="" disabled>
                  Select Language
                </option>
                {props.languages?.map((item: any, index: any) => (
                  <option
                    key={index}
                    value={item.id}
                    disabled={!teachable?.includes(item?.name)}
                  >
                    {item.name}
                  </option>
                ))}
              </select>
              {values.error.language && (
                <p className="error">{values.error.language}</p>
              )}
            </label>

            <label className="formLabel">
              Minimum Price (USD)
              <p className="inputSubtitle">
                The cheapest package plan available for this lesson
              </p>
              <input
                placeholder="e.g 30"
                type="number"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
              />
              {values.error.minPrice && (
                <p className="error">{values.error.minPrice}</p>
              )}
            </label>
          </div>

          <div className="formSection">
            <p className="sectionTitle">Add Lesson Packages</p>

            {packages.map((pkg: any, index: any) => (
              <div key={index} className="packageGroup">
                <label className="formLabel">
                  Price
                  <input
                    placeholder="e.g 4"
                    type="number"
                    value={pkg.price}
                    onChange={(e) =>
                      handlePackageChange(
                        index,
                        "price",
                        parseInt(e.target.value),
                      )
                    }
                    className="packageInput"
                  />
                </label>

                <label className="formLabel">
                  Duration
                  <select
                    className="packageInput"
                    value={pkg.duration}
                    onChange={(e) =>
                      handlePackageChange(
                        index,
                        "duration",
                        parseInt(e.target.value),
                      )
                    }
                  >
                    <option value={30}>30 minutes</option>
                    <option value={60}>60 minutes</option>
                  </select>
                  {values.error[`packageDuration_${index}`] && (
                    <p className="error">
                      {values.error[`packageDuration_${index}`]}
                    </p>
                  )}
                </label>

                <label className="formLabel">
                  Quantity
                  <input
                    placeholder="e.g 30"
                    type="number"
                    value={pkg.quantity}
                    onChange={(e) =>
                      handlePackageChange(
                        index,
                        "quantity",
                        parseInt(e.target.value),
                      )
                    }
                    className="packageInput"
                  />
                </label>

                <FontAwesomeIcon
                  className="deleteIcon"
                  icon={["fas", "trash"]}
                  onClick={() => removePackage(index)}
                />

                {values.error[`packagePrice_${index}`] && (
                  <p className="error">
                    {values.error[`packagePrice_${index}`]}
                  </p>
                )}

                {values.error[`packageQuantity_${index}`] && (
                  <p className="error">
                    {values.error[`packageQuantity_${index}`]}
                  </p>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={addPackage}
              className="addPackageButton"
            >
              + add package
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="modalActions">
          <button
            type="button"
            onClick={clearHandler}
            className="discardButton"
          >
            Discard
          </button>

          <button type="submit" className="saveButton" disabled={isPending}>
            {isPending ? (
              <span>
                <ClipLoader color="white" size={15} /> Saving
              </span>
            ) : (
              "Save"
            )}
          </button>
        </div>

        {values.submitError && <p className="error">{values.submitError}</p>}
      </form>
    </Modal>
  );
};
